import { useState, useEffect } from "react";
import { Loader2, Plus, Trash2, Pencil, Package, X } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { formatGs } from "@/lib/format";

const EMPTY = { name: "", slug: "", description: "", icon: "trending-up", active: true, min_quantity: 100, max_quantity: 100000, packages: [], supplier_api: "" };
const ICONS = ["trending-up", "users", "heart", "eye", "play"];

const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const Services = () => {
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); api.get("/admin/services").then((r) => setServices(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get("/admin/providers").then((r) => setProviders(r.data)); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (s) => { setForm({ ...s, supplier_api: s.supplier_api || "" }); setEditing(s.id); };

  const setPkg = (i, key, val) => {
    const pkgs = [...form.packages];
    const numeric = ["quantity", "price", "cost", "original_price"].includes(key);
    pkgs[i] = { ...pkgs[i], [key]: numeric ? Number(val) : val };
    setForm({ ...form, packages: pkgs });
  };
  const addPkg = () => setForm({ ...form, packages: [...form.packages, { quantity: 0, price: 0, original_price: 0, cost: 0, label: "", provider_id: "", provider_service_id: "" }] });
  const removePkg = (i) => setForm({ ...form, packages: form.packages.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!form.name) return toast.error("Ingresá el nombre");
    setSaving(true);
    const payload = { ...form, slug: form.slug || slugify(form.name), supplier_api: form.supplier_api || null };
    try {
      if (editing === "new") await api.post("/admin/services", payload);
      else await api.put(`/admin/services/${editing}`, payload);
      toast.success("Servicio guardado");
      setEditing(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este servicio?")) return;
    await api.delete(`/admin/services/${id}`);
    toast.success("Servicio eliminado");
    load();
  };

  const toggle = async (s) => {
    await api.put(`/admin/services/${s.id}`, { ...s, active: !s.active, supplier_api: s.supplier_api || null });
    load();
  };

  return (
    <div>
      <PageHeader title="Servicios" subtitle="Gestioná servicios, paquetes, precios y costos internos"
        action={<Button onClick={openNew} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="new-service-btn"><Plus className="h-4 w-4 mr-1" /> Nuevo servicio</Button>} />

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>
        : services.length === 0 ? <EmptyState icon={Package} text="Sin servicios." />
        : (
          <div className="grid md:grid-cols-2 gap-4">
            {services.map((s) => (
              <div key={s.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`service-item-${s.slug}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading font-bold text-slate-900">{s.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${s.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{s.active ? "Activo" : "Inactivo"}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 max-w-xs">{s.description}</p>
                  </div>
                  <Switch checked={s.active} onCheckedChange={() => toggle(s)} data-testid={`toggle-${s.slug}`} />
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(s.packages || []).map((p) => (
                    <span key={p.id || p.quantity} className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-slate-600">
                      {p.quantity} · {formatGs(p.price)} <span className="text-slate-400">(c: {formatGs(p.cost)})</span>
                    </span>
                  ))}
                </div>
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" size="sm" className="rounded-lg" onClick={() => openEdit(s)} data-testid={`edit-${s.slug}`}><Pencil className="h-4 w-4 mr-1" /> Editar</Button>
                  <Button variant="outline" size="sm" className="rounded-lg text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => del(s.id)} data-testid={`delete-${s.slug}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing === "new" ? "Nuevo servicio" : "Editar servicio"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || slugify(e.target.value) })} className="rounded-lg mt-1" data-testid="svc-name" /></div>
            <div><Label>Slug (URL)</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} className="rounded-lg mt-1" data-testid="svc-slug" /></div>
            <div><Label>Descripción</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-lg mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ícono</Label>
                <select value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className="w-full rounded-lg border border-slate-200 h-10 px-3 text-sm mt-1">
                  {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div><Label>API proveedor (opcional)</Label><Input value={form.supplier_api} onChange={(e) => setForm({ ...form, supplier_api: e.target.value })} className="rounded-lg mt-1" placeholder="Futuro" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Cant. mínima</Label><Input type="number" value={form.min_quantity} onChange={(e) => setForm({ ...form, min_quantity: Number(e.target.value) })} className="rounded-lg mt-1" /></div>
              <div><Label>Cant. máxima</Label><Input type="number" value={form.max_quantity} onChange={(e) => setForm({ ...form, max_quantity: Number(e.target.value) })} className="rounded-lg mt-1" /></div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Paquetes</Label>
                <Button size="sm" variant="outline" onClick={addPkg} className="rounded-lg h-8" data-testid="add-pkg"><Plus className="h-3.5 w-3.5 mr-1" /> Agregar</Button>
              </div>
              <div className="space-y-3">
                {form.packages.map((p, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-2">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div><span className="text-xs text-slate-400">Cantidad</span><Input type="number" value={p.quantity} onChange={(e) => setPkg(i, "quantity", e.target.value)} className="rounded-lg h-9 mt-1" data-testid={`pkg-qty-${i}`} /></div>
                      <div><span className="text-xs text-slate-400">Precio real (tachado)</span><Input type="number" value={p.original_price || 0} onChange={(e) => setPkg(i, "original_price", e.target.value)} className="rounded-lg h-9 mt-1" placeholder="0 = sin descuento" data-testid={`pkg-original-${i}`} /></div>
                      <div><span className="text-xs text-slate-400">Precio con descuento</span><Input type="number" value={p.price} onChange={(e) => setPkg(i, "price", e.target.value)} className="rounded-lg h-9 mt-1" data-testid={`pkg-price-${i}`} /></div>
                      <div className="flex items-end gap-1">
                        <div className="flex-1"><span className="text-xs text-slate-400">Costo interno</span><Input type="number" value={p.cost} onChange={(e) => setPkg(i, "cost", e.target.value)} className="rounded-lg h-9 mt-1" data-testid={`pkg-cost-${i}`} /></div>
                        <button onClick={() => removePkg(i)} className="text-rose-500 p-1 h-9"><X className="h-4 w-4" /></button>
                      </div>
                    </div>
                    {(p.original_price || 0) > (p.price || 0) && p.price > 0 && (
                      <p className="text-xs text-emerald-600">Descuento mostrado al cliente: -{Math.round((1 - p.price / p.original_price) * 100)}%</p>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-xs text-slate-400">Proveedor (entrega auto)</span>
                        <select value={p.provider_id || ""} onChange={(e) => setPkg(i, "provider_id", e.target.value)} className="w-full rounded-lg border border-slate-200 h-9 px-2 text-sm mt-1" data-testid={`pkg-provider-${i}`}>
                          <option value="">— Sin proveedor —</option>
                          {providers.map((pr) => <option key={pr.id} value={pr.id}>{pr.name}</option>)}
                        </select>
                      </div>
                      <div><span className="text-xs text-slate-400">ID servicio del proveedor</span><Input value={p.provider_service_id || ""} onChange={(e) => setPkg(i, "provider_service_id", e.target.value)} className="rounded-lg h-9 mt-1" placeholder="Ej: 1234" data-testid={`pkg-svcid-${i}`} /></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-lg">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="save-service">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Services;
